import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Loader2 } from 'lucide-react';

const SetupSheet = ({ hasAccess, onRequestAccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    entity: '',
    currency: '',
    fyStartMonth: '',
    fyStartYear: '',
    openingCash: '',
    openingEquity: ''
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/sheets/Setup`);
      const data = response.data.data;
      
      // Find the data row (row with "Accubee" or after headers)
      let dataRow = null;
      for (let i = 0; i < data.length; i++) {
        if (data[i][0] === 'Accubee' || (i > 2 && data[i][0])) {
          dataRow = data[i];
          break;
        }
      }
      
      if (dataRow) {
        setEditData({
          entity: dataRow[0] || '',
          currency: dataRow[1] || '',
          fyStartMonth: dataRow[2] || '',
          fyStartYear: dataRow[3] || '',
          openingCash: dataRow[4] || '',
          openingEquity: dataRow[5] || ''
        });
      }
    } catch (error) {
      console.error('Failed to load setup data:', error);
    } finally {
      setLoading(false);
    }
  };

  const autoSave = async () => {
    if (!hasAccess) {
      onRequestAccess(); // Show payment dialog
      return;
    }

    setSaving(true);
    try {
      const month = parseInt(editData.fyStartMonth);
      if (month < 1 || month > 12) {
        toast.error('FY Start Month must be between 1 and 12');
        setSaving(false);
        return;
      }

      await axios.post(`${API}/sheets/update`, {
        sheet_name: 'Setup',
        range: 'A4:F4',
        values: [[
          editData.entity,
          editData.currency,
          editData.fyStartMonth,
          editData.fyStartYear,
          editData.openingCash,
          editData.openingEquity
        ]]
      });
      
      toast.success('Saved successfully');
      setTimeout(fetchData, 1000);
    } catch (error) {
      toast.error('Auto-save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    if (!hasAccess) {
      onRequestAccess(); // trigger payment popup
      return;
    }
    setEditData({ ...editData, [field]: value });
  };

  if (loading) {
    return <div className="p-6 text-slate-600">Loading setup data...</div>;
  }

  return (
    <div className="space-y-6 fade-in">
      {saving && (
        <div className="fixed top-20 right-6 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Saving...</span>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-6">AOP Builder - Setup</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Entity</label>
            <Input
              data-testid="setup-entity"
              value={editData.entity}
              onChange={(e) => handleChange('entity', e.target.value)}
              onBlur={() => autoSave()}
              placeholder="Accubee"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Currency</label>
            <Input
              data-testid="setup-currency"
              value={editData.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              onBlur={() => autoSave()}
              placeholder="₹"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">FY Start Month (1-12)</label>
            <Input
              data-testid="setup-month"
              type="number"
              min="1"
              max="12"
              value={editData.fyStartMonth}
              onChange={(e) => handleChange('fyStartMonth', e.target.value)}
              onBlur={() => autoSave()}
              placeholder="4"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">FY Start Year</label>
            <Input
              data-testid="setup-year"
              type="number"
              value={editData.fyStartYear}
              onChange={(e) => handleChange('fyStartYear', e.target.value)}
              onBlur={() => autoSave()}
              placeholder="2025"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Opening Cash</label>
            <Input
              data-testid="setup-cash"
              type="number"
              value={editData.openingCash}
              onChange={(e) => handleChange('openingCash', e.target.value)}
              onBlur={() => autoSave()}
              placeholder="5000000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Opening Equity</label>
            <Input
              data-testid="setup-equity"
              type="number"
              value={editData.openingEquity}
              onChange={(e) => handleChange('openingEquity', e.target.value)}
              onBlur={() => autoSave()}
              placeholder="10000000"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupSheet;
